import './ui.css';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Icon } from './icons';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  /** When set, renders the error (with an icon) instead of the hint and flags the control invalid. */
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Form field wrapper: label + control + hint/error. Renders as a <label> so the
 * label text focuses the nested control. Wrap a single control.
 */
export function Field({ label, hint, error, required, children, className }: FieldProps) {
  const cls = ['ui-field', error && 'ui-field--invalid', className].filter(Boolean).join(' ');
  return (
    <label className={cls}>
      {label && (
        <span className="ui-field__label">
          {label}
          {required && <span className="ui-field__req" aria-hidden>*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="ui-field__error" role="alert">
          <Icon name="warning" size={13} />
          {error}
        </span>
      ) : hint ? (
        <span className="ui-field__hint">{hint}</span>
      ) : null}
    </label>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`ui-input${className ? ` ${className}` : ''}`} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`ui-textarea${className ? ` ${className}` : ''}`} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={`ui-select${className ? ` ${className}` : ''}`} {...rest}>
      {children}
    </select>
  );
}
